import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperclipIcon } from '@utils/icons';
import { formatFileSize } from '@utils/formatFileSize';
import type { Attachment, FileAttachment, ImageAttachment, VoiceAttachment } from '@interfaces/ICommsApi';
import type { IPendingAttachment } from '../../../hooks/useComposerAttachments';
import { downloadAttachment, useAttachmentUrl } from '../../../hooks/useAttachmentUrl';
import { VoicePlayer } from '../../VoicePlayer/VoicePlayer';
import styles from './AttachmentView.module.css';

function ImageAttachmentView({ siteId, attachment }: { siteId: string | undefined; attachment: ImageAttachment }) {
  const { t } = useTranslation();
  const { url, loading, error } = useAttachmentUrl(siteId, attachment.id);
  if (error) return <div className={styles.attachmentError}>{t('comms.attachment.loadError')}</div>;
  if (loading || !url) return <div className={styles.imagePlaceholder} aria-busy="true" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className={styles.imageLink}>
      <img src={url} alt={attachment.name} className={styles.image} />
    </a>
  );
}

function VoiceAttachmentView({ siteId, attachment }: { siteId: string | undefined; attachment: VoiceAttachment }) {
  const { t } = useTranslation();
  const { url, loading, error } = useAttachmentUrl(siteId, attachment.id);
  if (error) return <div className={styles.attachmentError}>{t('comms.attachment.loadError')}</div>;
  if (loading || !url) return <div className={styles.voice} aria-busy="true" />;
  return <VoicePlayer url={url} durationMs={attachment.durationMs} className={styles.voice} />;
}

function FileAttachmentView({ siteId, attachment }: { siteId: string | undefined; attachment: FileAttachment }) {
  const [busy, setBusy] = useState(false);
  async function handleDownload() {
    if (!siteId || busy) return;
    setBusy(true);
    try {
      await downloadAttachment(siteId, attachment.id, attachment.name);
    } catch {
      // Download failures are transient/permission issues; the chip stays.
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" className={styles.fileChip} onClick={handleDownload} disabled={busy}>
      <PaperclipIcon size={14} />
      <span className={styles.fileName}>{attachment.name}</span>
      <span className={styles.fileSize}>{formatFileSize(attachment.size)}</span>
    </button>
  );
}

export function AttachmentView({ siteId, attachment }: { siteId: string | undefined; attachment: Attachment }) {
  switch (attachment.kind) {
    case 'image':
      return <ImageAttachmentView siteId={siteId} attachment={attachment} />;
    case 'voice':
      return <VoiceAttachmentView siteId={siteId} attachment={attachment} />;
    case 'file':
      return <FileAttachmentView siteId={siteId} attachment={attachment} />;
  }
}

/** An attachment still on this device (sending, or failed to send), shown from its own bytes. */
export function LocalAttachmentView({ attachment }: { attachment: IPendingAttachment }) {
  switch (attachment.kind) {
    case 'image':
      return <img src={attachment.previewUrl} alt={attachment.name} className={styles.image} />;
    case 'voice':
      return <VoicePlayer url={attachment.previewUrl} durationMs={attachment.durationMs ?? 0} className={styles.voice} />;
    case 'file':
      return (
        <span className={`${styles.fileChip} ${styles.fileChipLocal}`}>
          <PaperclipIcon size={14} />
          <span className={styles.fileName}>{attachment.name}</span>
          {attachment.blob && <span className={styles.fileSize}>{formatFileSize(attachment.blob.size)}</span>}
        </span>
      );
  }
}
